<template>
  <div class="card">
    <h3 class="card-title">{{ $t('upload.title') }}</h3>
    
    <div class="upload-area" @drop="handleDrop" @dragover.prevent @dragenter.prevent>
      <div v-if="!imageUrl" class="upload-placeholder">
        <div class="upload-icon">📷</div>
        <p>{{ $t('upload.placeholder') }}</p>
        <input
          ref="fileInput"
          type="file"
          accept="image/*"
          @change="handleFileSelect"
          style="display: none"
        />
        <button class="btn-primary" @click="triggerFileSelect">
          {{ $t('upload.selectImage') }}
        </button>
      </div>
      
      <div v-else class="image-preview">
        <img :src="imageUrl" :alt="$t('upload.title')" class="preview-image" />
        <div class="image-actions">
          <button class="btn-secondary" @click="clearImage">{{ $t('upload.clearImage') }}</button>
          <button class="btn-primary" @click="extractColors" :disabled="extracting">{{ extracting ? $t('upload.extracting') : $t('upload.extractColors') }}</button>
        </div>
        <div v-if="extracting" class="progress" :aria-label="$t('upload.progress')">
          <div class="progress-inner" :style="{ width: extractProgress + '%' }"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useColorStore } from '../stores/colorStore'
import { extractColorGrid, generatePreviewImage } from '../utils/colorUtils'
import { extractColorGridAsync } from '../utils/colorUtils'
import { usePreviewStore } from '@/stores/previewStore'
import { analytics } from '../utils/analytics'

const colorStore = useColorStore()
const previewStore = usePreviewStore()
const fileInput = ref<HTMLInputElement>()

const imageUrl = computed(() => colorStore.imageUrl)
const extracting = ref(false)
const extractProgress = ref(0)

function triggerFileSelect() {
  fileInput.value?.click()
}

function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement
  const file = target.files?.[0]
  if (file && file.type.startsWith('image/')) {
    colorStore.setImage(file)
    // 跟踪图片上传事件
    analytics.trackImageUpload(file.type, file.size)
  }
}

function handleDrop(event: DragEvent) {
  event.preventDefault()
  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    const file = files[0]
    if (file.type.startsWith('image/')) {
      colorStore.setImage(file)
      // 跟踪图片拖拽上传事件
      analytics.trackImageUpload(file.type, file.size)
    }
  }
}

function clearImage() {
  colorStore.clearAll()
}

function extractColors() {
  const img = new Image()
  img.onload = () => {
    // 只有在没有提取过颜色时才自动设置网格尺寸
    if (!colorStore.extractedColors) {
      const [optW, optH] = computeOptimalGridSize(img.naturalWidth, img.naturalHeight)
      colorStore.setGridSize(optW, optH)
    }

    extracting.value = true
    colorStore.setExtracting(true)
    extractProgress.value = 0
    extractColorGridAsync(
      img,
      colorStore.gridSize.width,
      colorStore.gridSize.height,
      (p) => { extractProgress.value = Math.round(p * 100) }
    ).then((colors) => {
      colorStore.setExtractedColors({
        width: colorStore.gridSize.width,
        height: colorStore.gridSize.height,
        pixels: colors
      })
      extracting.value = false
      colorStore.setExtracting(false)
      
      // 跟踪颜色提取事件
      const uniqueColors = new Set(colors.map(c => `${c.r},${c.g},${c.b}`)).size
      analytics.trackImageExtractColors(colorStore.gridSize.width, colorStore.gridSize.height, uniqueColors)
      // previewStore.renderAllPreviews({
      //     pixelSize: colorStore.previewPixelSize,
      //     algorithm: colorStore.colorMatchingAlgorithm
      //   })
    }).catch(() => {
      extracting.value = false
      colorStore.setExtracting(false)
    })
  }
  img.src = imageUrl.value
}

function computeOptimalGridSize(imgW: number, imgH: number): [number, number] {
  const aspect = imgW / imgH
  const MAX_DIM = 200        // 单边上限
  const MIN_DIM = 32         // 单边下限
  const MAX_CELLS = 40000    // 总像素点上限（性能）

  let w: number
  let h: number

  if (imgW >= imgH) {
    w = Math.min(MAX_DIM, Math.max(MIN_DIM, Math.round(MAX_DIM)))
    h = Math.max(MIN_DIM, Math.round(w / aspect))
  } else {
    h = Math.min(MAX_DIM, Math.max(MIN_DIM, Math.round(MAX_DIM)))
    w = Math.max(MIN_DIM, Math.round(h * aspect))
  }

  // 若超出像素点上限，则同比例缩小
  const cells = w * h
  if (cells > MAX_CELLS) {
    const scale = Math.sqrt(MAX_CELLS / cells)
    w = Math.max(MIN_DIM, Math.round(w * scale))
    h = Math.max(MIN_DIM, Math.round(h * scale))
  }

  // 兜底，避免为0
  w = Math.max(1, w)
  h = Math.max(1, h)
  return [w, h]
}
</script>

<style scoped>
.upload-area {
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
  transition: border-color 0.2s ease;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.upload-area:hover {
  border-color: #667eea;
}

.upload-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.upload-icon {
  font-size: 3rem;
  opacity: 0.6;
}

.upload-placeholder p {
  color: #6c757d;
  margin: 0;
}

.image-preview {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.preview-image {
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

.image-actions {
  display: flex;
  gap: 1rem;
}

.progress { width: 100%; max-width: 420px; height: 10px; background: #e9ecef; border-radius: 999px; overflow: hidden; margin-top: .5rem; }
.progress-inner { height: 100%; background: linear-gradient(90deg, #667eea, #764ba2); transition: width .15s ease; }
</style>
